import React, { useReducer, useContext, useCallback } from 'react';
import axios from '../../components/Auth/Components/AxiosConfig';
import ReportContext from './reportContext';
import ReportReducer from './reportReducer';
import MainContext from '../main/MainContext';
import builderContextNew from '../builderCustom/builderContextNew';
import { GET_REPORT, REPORT_REFRESH } from '../types';
import AuthContext from '../auth/AuthContext';
import {
  LITYPE,
  AUDTYPE,
  MEDIATYPE,
  CROSSTYPE,
  BEHAVIOURTYPE,
  CROSSMEDIATYPE,
} from '../../components/Utilities/NamingConversions';
import { format, parseISO } from 'date-fns';
import CrossMediaContext from '../crossMedia/crossMediaContext';

const defaultLoadingState = { main: 0 };

function ReportState(props) {
  const authContext = useContext(AuthContext);
  const mainContext = useContext(MainContext);
  const crossContext = useContext(CrossMediaContext);
  const builderContextCustom = useContext(builderContextNew);

  const initialState = {
    reports: [],
    orgReports: [],
    sharedReports: [],
    subscription: [],
    // loading: false,
    loading: defaultLoadingState,
    link: null,
    id: null,
    version: 4,
    allowPreviousVersion: false, // allow a buffer period for deprecated versions
    inReport: false,
    tabs: [],
    activeTab: 0,
  };

  const [state, dispatch] = useReducer(ReportReducer, initialState);

  const incrementLoading = (type = 'main') => {
    dispatch({
      type: 'INCREMENT_LOADING',
      payload: {
        type: type,
      },
    });
  };

  const decrementLoading = (type = 'main') => {
    dispatch({
      type: 'DECREMENT_LOADING',
      payload: {
        type: type,
      },
    });
  };

  const apiCall = async (method, url, data = {}) => {
    try {
      incrementLoading('main');
      const token = await authContext.getIdToken();
      const res = await axios(token)[method](url, {
        ...data,
        countryCode: authContext.userCountryCode,
        kwargs: mainContext.getPostKWArgs(),
      });
      return res.data;
    } catch (err) {
      mainContext.handleError(err);
      return null;
    } finally {
      decrementLoading('main');
    }
  };

  const reportRefresh = () => {
    mainContext.dispatch({
      type: REPORT_REFRESH,
    });
  };

  const saveReport = async ({
    title,
    tabName,
    type,
    description,
    layout,
    checked,
    theme,
    tabDefaultSuffix,
  }) => {
    const reportType = (type) => {
      switch (type) {
        case LITYPE:
          return {
            content: JSON.stringify(mainContext.mapdata),
            defaultTabName: 'Location Intelligence',
            layout: layout,
            checked: checked,
          };

        case CROSSTYPE: // cross tab
          return {
            content: JSON.stringify({
              pivotdata: mainContext.pivotdata,
              pivotCond: mainContext.pivotCond,
              pivotVars: mainContext.pivotVars,
              pivotRegion: mainContext.pivotRegion,
            }),
            defaultTabName: 'Audience Crosstab',
          };

        case BEHAVIOURTYPE:
          return {
            content: JSON.stringify(mainContext.indexdata),
            // defaultTabName: 'Behavior Discovery',
            defaultTabName:
              tabDefaultSuffix && tabDefaultSuffix.length > 0
                ? `Behavior Discovery - ${tabDefaultSuffix}`
                : 'Behavior Discovery',
            layout: layout,
          };

        case CROSSMEDIATYPE: // cross media plan
          // store current tab's context so user can resume when loading report
          crossContext.tabs[crossContext.selectedTab].context =
            crossContext.dumpContext(true);

          return {
            content: JSON.stringify({
              tabs: crossContext.tabs,
            }),
            defaultTabName: 'Cross Media Plan',
          };

        case MEDIATYPE:
          return {
            content: JSON.stringify({
              mediaUsage: mainContext.mediaUsage,
              mediaChannels: mainContext.mediaChannels,
            }),
            defaultTabName: 'Media Usage',
            layout: layout,
          };

        case AUDTYPE:
          return {
            content: JSON.stringify(mainContext.demSummary),
            defaultTabName: 'Audience Overview',
          };

        default:
          return {};
      }
    };
    const content = reportType(type);
    content.audienceSize = mainContext.audienceSize;
    if (tabName === undefined || tabName === '' || tabName === null) {
      tabName = content.defaultTabName;
    }

    const data = await apiCall('post', '/save_report', {
      title: title,
      tabName: tabName,
      content: content,
      idsData: mainContext.dumpIdsData(),
      countryCode: authContext.userCountryCode,
      kwargs: mainContext.getPostKWArgs(),
      audienceSize: mainContext.audienceSize,
      kind: type,
      version: state.version,
      description: description,
      audience: !!builderContextCustom.advanced
        ? {
            code: mainContext.mapcode,
            // sqlWhere: builderContext.sqlWhere,
            // logic: JSON.stringify(builderContext.logic),
            config: JSON.stringify(builderContextCustom.state),
            newVersion: true,
          }
        : {
            code: mainContext.mapcode,
            display: mainContext.mapdisplay,
            cond: mainContext.mapcond,
            newVersion: false,
          },
      names: mainContext.defineCus.names,
      theme: theme,
    });
    await getAllReports();
    return data;
  };

  async function getAllReports() {
    function processReportData(data) {
      return (data || []).map((p) => ({
        ...p,
        title: p.title,
        last_modified: p.last_modified
          ? format(parseISO(p.last_modified), 'E MMM dd yyyy')
          : '',
        user_id: authContext.userInfo ? p.user_id : undefined,
      }));
    }

    // fetch user reports, company reports, and shared reports in parallel
    const [userData, companyData, sharedData] = await Promise.all([
      apiCall('post', '/get_reports'),
      apiCall('post', '/get_company_reports'),
      apiCall('post', '/get_shared_reports'),
    ]);

    const reports = processReportData(userData);
    const companyReports = processReportData(companyData);
    const sharedReports = processReportData(sharedData);

    dispatch({
      type: GET_REPORT,
      payload: {
        reports: reports,
        orgReports: companyReports,
        sharedReports: sharedReports,
      },
    });
  }

  async function getSingleReport(id) {
    const data = (await apiCall('post', '/get_report_tabs', { id: id })) || [];
    data.forEach((tab) => {
      tab.content =
        typeof tab.content === 'string' ? JSON.parse(tab.content) : tab.content;
    });
    dispatch({ type: 'SET_TABS', payload: data });
    return data;
  }

  const deleteTab = async (deleteKey) => {
    const data = await apiCall('post', '/delete_tab', deleteKey);
    if (data) {
      deleteKey.child
        ? dispatch({
            type: 'DELETE_TAB',
            payload: deleteKey.child,
          })
        : getAllReports();
    }
  };

  async function getReportFromLink(link) {
    const data =
      (await apiCall('post', '/get_report_from_link', {
        link: link,
      })) || {};
    if (data.title) {
      data.title = decodeURIComponent(data.title);
    }
    return data;
  }

  async function getReportDataFromLink(link) {
    const data =
      (await apiCall('post', '/get_report_data_from_link', { link: link })) ||
      [];
    data.forEach((tab) => {
      tab.content =
        typeof tab.content === 'string' ? JSON.parse(tab.content) : tab.content;
    });
    dispatch({ type: 'SET_TABS', payload: data });
    return data;
  }

  const storeShareLink = useCallback((link, id) => {
    dispatch({ type: 'STORE_LINK', payload: { link: link, id: id } });
  }, []);

  const resetLink = () => {
    dispatch({ type: 'RESET_LINK' });
  };

  const deepRefresh = () => {
    mainContext.refresh();
    builderContextCustom.refreshAdvancedQB();
    dispatch({ type: 'SET_TABS', payload: [] });
  };

  const clearUser = () => {
    deepRefresh();
    dispatch({ type: 'GET_REPORT', payload: [] });
  };

  const updateTabNames = async (newTabs, reportID) => {
    const data = await Promise.all(
      newTabs.map(async (tab) => {
        const tabData = await apiCall('post', '/update_report_tab_title', {
          tabId: tab.key,
          reportId: reportID,
          tabTitle: tab.tabName,
        });

        return tabData;
      })
    );

    const errorResponsesData = data.filter((item) => item.status_code !== 200);
    if (
      errorResponsesData.length > 0 &&
      errorResponsesData.length < data.length
    ) {
      return `Some tabs could not be renamed: ${errorResponsesData[0]?.status}`;
    }

    if (errorResponsesData.length === data.length) {
      return `Could not rename tabs: ${errorResponsesData[0]?.status}`;
    }

    const updated = state.tabs.map((t) => {
      const foundTab = newTabs.find((newTab) => newTab.key === t.key);
      if (!foundTab) {
        return t;
      }

      return { ...t, tabName: foundTab.tabName };
    });

    dispatch({
      type: 'UPDATE_TABNAME',
      payload: updated,
    });

    return '';
  };

  const updateOrder = async (order, reportid) => {
    const data = await apiCall('post', '/update_order', {
      order: order,
      reportid: reportid,
    });

    const updated = state.reports.map((r) =>
      r.id === reportid ? { ...r, order: order } : r
    );
    dispatch({ type: 'UPDATE_ORDER', payload: updated });

    return data;
  };

  const saveNewTitle = async (title, id, description) => {
    return await apiCall('post', '/update_report_title', {
      title: title,
      reportid: id,
      description: description,
    });
  };

  const setSubscription = (subscription) => {
    dispatch({ type: 'SET_SUB', payload: subscription ? subscription : [] });
  };

  const setUserInfo = (userName) => {
    dispatch({ type: 'SET_USERNAME', payload: userName });
  };

  const setInReport = (inReport) => {
    dispatch({ type: 'SET_INREPORT', payload: inReport });
  };

  const ShareWithOrg = async ({ reportID, sharedWithValue }) => {
    const data = await apiCall('post', '/share_report_with_org', {
      company: authContext.userInfo?.company,
      report_id: reportID,
      share: sharedWithValue,
    });
    getAllReports();
    return data;
  };

  const setActiveTab = (tab) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: Math.max(tab - 1, 0) });
  };

  return (
    <ReportContext.Provider
      value={{
        reports: state.reports,
        orgReports: state.orgReports,
        sharedReports: state.sharedReports,
        subscription: state.subscription,
        loading: state.loading,
        loadingCounter: state.loadingCounter,
        link: state.link,
        id: state.id,
        version: state.version,
        allowPreviousVersion: state.allowPreviousVersion,
        inReport: state.inReport,
        tabs: state.tabs,
        activeTab: state.activeTab,
        reportRefresh,
        saveReport,
        getSingleReport,
        deleteTab,
        getAllReports,
        getReportFromLink,
        getReportDataFromLink,
        storeShareLink,
        resetLink,
        deepRefresh,
        updateOrder,
        updateTabNames,
        saveNewTitle,
        setSubscription,
        setUserInfo,
        setInReport,
        clearUser,
        ShareWithOrg,
        incrementLoading,
        decrementLoading,
        setActiveTab,
      }}
    >
      {props.children}
    </ReportContext.Provider>
  );
}

export default ReportState;
